import { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, BellRing, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  checkDueReminders,
} from '@/lib/notifications';

export default function NotificationSettings() {
  const { reminders, setSelectedNoteId } = useApp();
  const [permission, setPermission] = useState(getNotificationPermission());
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    localStorage.getItem('vaultnotes_notifications_enabled') === 'true'
  );

  // Update permission status
  useEffect(() => {
    setPermission(getNotificationPermission());
  }, []);

  // Check due reminders periodically
  useEffect(() => {
    if (!notificationsEnabled || permission !== 'granted') return;

    const checkReminders = () => {
      checkDueReminders(reminders, (reminder) => {
        // Navigate to the note when reminder is clicked
        if (reminder.note_id) {
          setSelectedNoteId(reminder.note_id);
        }
      });
    };

    // Check immediately and then every minute
    checkReminders();
    const interval = setInterval(checkReminders, 60000);

    return () => clearInterval(interval);
  }, [reminders, notificationsEnabled, permission, setSelectedNoteId]);

  const handleEnableNotifications = async () => {
    if (!isNotificationSupported()) {
      toast.error('Notifications are not supported in your browser');
      return;
    }

    const newPermission = await requestNotificationPermission();
    setPermission(newPermission);

    if (newPermission === 'granted') {
      setNotificationsEnabled(true);
      localStorage.setItem('vaultnotes_notifications_enabled', 'true');
      toast.success('Notifications enabled!');
      
      // Show test notification
      showNotification('VaultNotes', {
        body: 'Notifications are now enabled! You will receive alerts for your reminders.',
        tag: 'test-notification',
      });
    } else if (newPermission === 'denied') {
      toast.error('Notification permission denied. Enable in browser settings.');
    }
  };

  const handleDisableNotifications = () => {
    setNotificationsEnabled(false);
    localStorage.setItem('vaultnotes_notifications_enabled', 'false');
    toast.info('Notifications disabled');
  };

  const handleToggle = (enabled) => {
    if (enabled) {
      handleEnableNotifications();
    } else {
      handleDisableNotifications();
    }
  };

  if (!isNotificationSupported()) {
    return (
      <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm border border-border">
        <BellOff className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Notifications not supported in this browser</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-accent-cyan" />
        <h3 className="text-sm font-semibold">Reminder Notifications</h3>
      </div>
      
      <p className="text-xs text-muted-foreground leading-relaxed">
        Receive browser notifications when your reminders are due, even when VaultNotes is in the background.
      </p>

      <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border">
        <div className="flex items-center gap-3">
          {notificationsEnabled && permission === 'granted' ? (
            <BellRing className="w-4 h-4 text-green-500" />
          ) : (
            <BellOff className="w-4 h-4 text-muted-foreground" />
          )}
          <Label className="text-sm">Browser Notifications</Label>
        </div>
        
        <div className="flex items-center gap-2">
          {permission === 'denied' ? (
            <span className="text-xs text-red-400">Blocked</span>
          ) : permission === 'granted' && notificationsEnabled ? (
            <span className="text-xs text-green-500 flex items-center gap-1">
              <Check className="w-3 h-3" /> Enabled
            </span>
          ) : null}
          
          <Switch
            checked={notificationsEnabled && permission === 'granted'}
            onCheckedChange={handleToggle}
            disabled={permission === 'denied'}
            data-testid="notification-toggle"
          />
        </div>
      </div>

      {permission === 'denied' && (
        <div className="text-xs text-muted-foreground p-2 bg-red-500/10 border border-red-500/20 rounded-sm">
          Notifications are blocked. To enable, click the lock icon in your browser's address bar and allow notifications for this site.
        </div>
      )}

      {permission === 'default' && (
        <Button 
          onClick={handleEnableNotifications} 
          size="sm" 
          variant="outline"
          className="w-full"
          data-testid="enable-notifications-btn"
        >
          <Bell className="w-3.5 h-3.5 mr-1.5" /> Enable Notifications
        </Button>
      )}

      {notificationsEnabled && permission === 'granted' && (
        <Button 
          onClick={() => showNotification('Test Notification', { body: 'Notifications are working!' })}
          size="sm"
          variant="ghost"
          className="w-full text-muted-foreground"
          data-testid="test-notification-btn"
        >
          <BellRing className="w-3.5 h-3.5 mr-1.5" /> Send Test Notification
        </Button>
      )}
    </div>
  );
}
